import { useEffect, useState, useCallback } from "react";
import { placeholderImage, toPublicStorageUrl } from "../config/imageConfig";
import { useNavigate, useLocation } from "react-router-dom";
import { useChatProducts } from "../context/ChatProductsContext";
import ProductDetailView from "./ProductDetailView";
import { getUserProfile } from "../utils/auth";

export default function ProductRecommendationPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { chatProducts } = useChatProducts();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadProducts = useCallback(() => {
    // Get products from context, location state, or sessionStorage
    // Check location.state first (highest priority) - includes timestamp to force updates
    if (location.state?.products && location.state.products.length > 0) {
      setProducts(location.state.products);
      // Also update sessionStorage for consistency
      sessionStorage.setItem(
        "orbiee_recommendation_products",
        JSON.stringify(location.state.products)
      );
    } else if (chatProducts && chatProducts.length > 0) {
      setProducts(chatProducts);
    } else {
      // Check sessionStorage for products from OrbieeFlow
      const storedProducts = sessionStorage.getItem("orbiee_recommendation_products");
      if (storedProducts) {
        try {
          const parsedProducts = JSON.parse(storedProducts);
          if (Array.isArray(parsedProducts) && parsedProducts.length > 0) {
            setProducts(parsedProducts);
            // Don't clear sessionStorage - keep it for potential re-renders
          }
        } catch (e) {
          console.warn("Failed to parse stored products", e);
          sessionStorage.removeItem("orbiee_recommendation_products");
        }
      }
    }
  }, [location.state, chatProducts]);

  useEffect(() => {
    setLoading(true);
    loadProducts();
    // Resolve loading after we've read from state/context/sessionStorage
    setLoading(false);
  }, [loadProducts, location.key]);

  // Listen for custom event to update products when already on the page
  useEffect(() => {
    const handleProductsUpdate = (event) => {
      const { products: newProducts } = event.detail || {};
      if (newProducts && Array.isArray(newProducts) && newProducts.length > 0) {
        setProducts(newProducts);
        // Update sessionStorage
        sessionStorage.setItem(
          "orbiee_recommendation_products",
          JSON.stringify(newProducts)
        );
      }
    };

    window.addEventListener("orbiee:productsUpdated", handleProductsUpdate);
    return () => {
      window.removeEventListener("orbiee:productsUpdated", handleProductsUpdate);
    };
  }, []);

  const handleProductClick = (product) => {
    if (!product?.id) return;

    // Let ChatBot know which product was clicked so it can prefill/send the name
    try {
      window.dispatchEvent(
        new CustomEvent("chat:productSelected", {
          detail: { product },
        })
      );
    } catch (e) {
      console.warn("Failed to notify chat of product click", e);
    }

    navigate(`/item/${product.id}`);
  };

  const getImageUrl = (rawUrl) => {
    if (!rawUrl) return null;
    return toPublicStorageUrl(rawUrl.trim());
  };

  const formatPrice = (price, currency = "USD") => {
    const numericPrice = parseFloat(price);
    if (Number.isNaN(numericPrice)) return "—";
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency,
        maximumFractionDigits: 2,
      }).format(numericPrice);
    } catch (e) {
      // Fallback if currency code is invalid
      return `${currency} ${numericPrice.toFixed(2)}`;
    }
  };

  const getSizes = (product) => {
    // Try to get sizes from various possible fields
    if (product.attributes?.size) {
      const sizeStr = Array.isArray(product.attributes.size)
        ? product.attributes.size.join(" ")
        : String(product.attributes.size);
      return sizeStr.split(/[,\s]+/).filter(Boolean);
    }
    if (product.size) {
      return Array.isArray(product.size) ? product.size : [product.size];
    }
    // Default sizes
    return ["XS", "S", "M", "L"];
  };

  const getRating = (product) => {

    return product.rating || product.rating_score || 4.5;
  };

  const getReviewCount = (product) => {
    return product.review_count || product.reviews || Math.floor(Math.random() * 200) + 50;
  };

  const isOnSale = (product) => {
    return (
      product.original_price &&
      product.price &&
      parseFloat(product.original_price) > parseFloat(product.price)
    );
  };

  const hasLabel = (product) => {
    return product.is_bestseller || isOnSale(product);
  };

  // Show loader until we've resolved products from state/context/storage.
  // Skeletons in the grid's own shape, so the page does not jump when the
  // real cards land.
  if (loading) {
    return (
      <div className="max-w-[1320px] mx-auto px-4 sm:px-6 py-10">
        <div className="ob-skel h-3 w-28 mb-4" />
        <div className="ob-skel h-9 w-64 mb-10" />
        <div className="ob-grid">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i}>
              <div
                className="ob-skel"
                style={{ aspectRatio: "3 / 4", borderRadius: "var(--ob-r)" }}
              />
              <div className="ob-skel h-2.5 w-16 mt-3" />
              <div className="ob-skel h-3 w-full mt-2" />
              <div className="ob-skel h-3 w-20 mt-2" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // No data or failed: show message only after loading is done
  if (products.length === 0) {
    return (
      <div className="max-w-[1320px] mx-auto px-4 sm:px-6 py-10 ob-viewin">
        <div className="ob-empty">
          <div className="ob-empty-glyph">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.6}
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-6 h-6"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3.5-3.5" />
            </svg>
          </div>
          <div className="ob-display text-[26px]" style={{ color: "var(--ob-ink)" }}>
            Nothing to show yet
          </div>
          <p className="text-[13px] mt-2 max-w-[360px] mx-auto leading-relaxed">
            We couldn&rsquo;t find any recommendations just now. Tell Orbiee what
            you are after in your own words and it will go looking.
          </p>
          <button
            type="button"
            className="ob-btn ob-btn-solid ob-btn-lg mt-6"
            onClick={() => navigate("/")}
          >
            Back to home
          </button>
        </div>
      </div>
    );
  }

  if (products.length === 1) {
    const singleProduct = products[0];

    const mockDetail = {
      product: {
        id: singleProduct.id,
        title: singleProduct.title || singleProduct.name,
        description: singleProduct.description || "",
        images: [
          singleProduct.images?.[0] ||
            singleProduct.image_url ||
            singleProduct.image ||
            null,
        ].filter(Boolean),
        price_info: {
          price: singleProduct.price_info?.price || singleProduct.price || 0,
          originalPrice:
            singleProduct.price_info?.original_price ||
            singleProduct.original_price ||
            null,
          currencyCode:
            singleProduct.price_info?.currencyCode ||
            singleProduct.price_info?.currency ||
            singleProduct.currency ||
            "USD",
        },
        attributes: {
          size: singleProduct.attributes?.size || singleProduct.size,
          color: singleProduct.attributes?.color || "N/A",
          fabric: singleProduct.attributes?.fabric,
          fit: singleProduct.attributes?.fit,
          rating: singleProduct.rating || 4.5,
          review_count: singleProduct.review_count || 100,
        },
        categories: singleProduct.categories || [],
      },
      variants: singleProduct.variants || [],
    };

    const userProfile = getUserProfile();
    const customerId = userProfile?.customer_id;

    return (
      <ProductDetailView
        productDetail={mockDetail}
        customerId={customerId}
        onBack={() => navigate(-1)} // or pass navigate
      />
    );
  }

  return (
    <div className="max-w-[1320px] mx-auto px-4 sm:px-6 py-8 ob-viewin">
      {/* ── Header ── */}
      <header className="mb-8">
        <span className="ob-chip ob-chip-accent">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.6}
            strokeLinecap="round"
            strokeLinejoin="round"
            className="w-[13px] h-[13px]"
          >
            <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9Z" />
          </svg>
          Orbiee found these
        </span>
        <h1 className="ob-display text-[clamp(1.85rem,4.4vw,2.6rem)] leading-[1.08] mt-3">
          Picked for you
        </h1>
        <p className="text-[13px] ob-ink2 mt-2 max-w-[540px] leading-relaxed">
          {products.length} piece{products.length === 1 ? "" : "s"} matched what
          you asked for. 
        </p>
      </header>

      {/* ── Grid ── */}
      <div className="ob-grid ob-stagger">
        {products.map((product, index) => {
          const imageUrl = getImageUrl(
            product.image ||
              product.image_url ||
              product.images?.[0] ||
              product.product?.image
          );
          const title =
            product.enhanced_name ||
            product.title ||
            product.product?.title ||
            product.name ||
            "Product";
          const currency =
            product.currency ||
            product.price_info?.currencyCode ||
            product.price_info?.currency ||
            product.product?.currency ||
            "USD";
          const price = parseFloat(product.price || product.product?.price || 0);
          const originalPrice = parseFloat(
            product.original_price ||
              product.product?.original_price ||
              product.price_info?.originalPrice ||
              0
          );
          const sizes = getSizes(product);
          const rating = getRating(product);
          const reviewCount = getReviewCount(product);
          const onSale = isOnSale(product);
          const isBestseller = product.is_bestseller || product.bestseller;
          const brand = product.brand || product.product?.brand || "";

          return (
            <article
              key={product.id || index}
              className="ob-card"
              onClick={() => handleProductClick(product)}
            >
              <div className="ob-shot">
                {/* The ordinal is what makes "add the second one" work — the
                    shopper can only say it if the numbering is on screen. */}
                <span className="ob-ord">{index + 1}</span>

                {hasLabel(product) && (
                  <span
                    className={`ob-tagline ${!isBestseller ? "ob-tagline-sale" : ""}`}
                  >
                    {isBestseller ? "Bestseller" : "Sale"}
                  </span>
                )}

                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={title}
                    loading="lazy"
                    decoding="async"
                    onError={(e) => {
                      e.target.src = placeholderImage(product.id || index, 400, 534);
                    }}
                  />
                ) : (
                  <div className="w-full h-full grid place-items-center ob-ink3 text-[11px]">
                    No image
                  </div>
                )}
              </div>

              <div className="ob-card-meta">
                {brand && <div className="ob-card-brand">{brand}</div>}
                <div className="ob-card-title">{title}</div>

                <div className="ob-card-price">
                  {formatPrice(price, currency)}
                  {onSale && originalPrice > price && (
                    <span className="ob-card-mrp">
                      {formatPrice(originalPrice, currency)}
                    </span>
                  )}
                </div>

                {/* One star plus the number reads faster at this size than
                    five glyphs the eye has to count. */}
                <div className="flex items-center gap-1 mt-1.5 ob-ink3 text-[10.5px]">
                  <svg
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="w-3 h-3"
                    style={{ color: "var(--ob-warn)" }}
                  >
                    <path d="M12 2.5l2.9 6 6.6.9-4.8 4.6 1.2 6.5L12 17.4 6.1 20.5l1.2-6.5L2.5 9.4l6.6-.9Z" />
                  </svg>
                  <span
                    className="font-semibold"
                    style={{ color: "var(--ob-ink-2)" }}
                  >
                    {rating.toFixed(1)}
                  </span>
                  <span>({reviewCount})</span>
                </div>

                {sizes.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {sizes.slice(0, 5).map((size, idx) => (
                      <span key={idx} className="ob-sizetag">
                        {size}
                      </span>
                    ))}
                    {sizes.length > 5 && (
                      <span className="ob-sizetag">+{sizes.length - 5}</span>
                    )}
                  </div>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
